import { AxiosError } from "axios";

import { isServer } from "solid-js/web";

import { handleError } from "@/hooks/useService";
import {
  getGetMsgParam,
  getHallHttpUrl,
  getPostMsgParam,
  getToken,
} from "@/lib/apiUtils";
import { configStore } from "@/stores/configStore";

import request from "./client";

// 缓存 Map
const cacheMap = new Map<string, { data: unknown; timestamp: number }>();

// 缓存配置
interface CacheConfig {
  // 缓存时间，单位毫秒，默认 5 分钟
  ttl?: number;
  // 是否强制刷新缓存
  forceRefresh?: boolean;
}

// 默认缓存时间 5 分钟
const DEFAULT_CACHE_TTL = 5 * 60 * 1000;

// 生成缓存 key
const generateCacheKey = (url: string, params?: unknown) => {
  try {
    // 先解码 URL
    const decodedUrl = decodeURIComponent(url);

    // 处理 URL 中的时间参数
    const cleanUrl = decodedUrl
      .replace(/[?&](?:m|t|timestamp|time|_t)=\d+/g, "")
      .replace(/[?&](?:time|date)[^&]*/g, "")
      .replace(/\?$/, "") // 移除末尾的问号
      .replace(/&+/g, "&") // 处理可能出现的连续 &
      .replace(/^&/, "") // 移除开头的 &
      .replace(/&$/, ""); // 移除结尾的 &

    // 处理参数
    let paramString = "";
    if (params) {
      // 如果是 FormData，转换为普通对象
      if (params instanceof FormData) {
        const formDataObj: Record<string, string> = {};
        params.forEach((value, key) => {
          formDataObj[key] = value.toString();
        });
        params = formDataObj;
      }

      // 过滤掉时间相关的参数并排序
      const filteredParams = Object.entries(params as Record<string, unknown>)
        .filter(
          ([key]) =>
            !(
              key === "t" ||
              key === "timestamp" ||
              key === "m" ||
              key === "time" ||
              key === "_t" ||
              key.includes("time") ||
              key.includes("date")
            ),
        )
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB));

      // 生成参数字符串
      paramString = filteredParams
        .map(([key, value]) => {
          // 处理特殊值
          if (value === null || value === undefined) return "";
          if (typeof value === "object") {
            try {
              return `${key}=${JSON.stringify(value)}`;
            } catch {
              return `${key}=[Object]`;
            }
          }
          return `${key}=${String(value)}`;
        })
        .filter(Boolean)
        .join("&");
    }

    // 生成最终的缓存 key
    const finalKey = `${new URL(cleanUrl).pathname}${paramString ? `?${paramString}` : ""}`;

    // 如果 key 太长，使用 hash
    if (finalKey.length > 1000) {
      const hash = Array.from(finalKey).reduce(
        (hash, char) => (hash << 5) - hash + char.charCodeAt(0),
        0,
      );
      return `${new URL(cleanUrl).pathname}?hash=${hash}`;
    }

    return finalKey;
  } catch (error) {
    console.error("Error generating cache key:", error);
    return url; // 如果出错，返回原始 URL
  }
};

// 检查缓存是否有效
const isCacheValid = (timestamp: number, ttl: number) => {
  return Date.now() - timestamp < ttl;
};

export const reqPost = <T, R>(
  reqParams: T,
  modName: string,
  funcName: string,
  isNeedLogin: boolean = false,
  onTimeout?: () => void,
  onError?: (error: AxiosError) => void,
  cacheConfig?: CacheConfig,
  onSuccess?: (data: R) => void,
) => {
  if (!reqParams || !modName || !funcName) {
    console.log("reqPost error ", reqParams, modName, funcName);
    return Promise.reject();
  }
  if (isNeedLogin) {
    const isLogin = getToken();
    if (!isLogin) {
      return;
    }
  }
  const url = getHallHttpUrl(modName, funcName, isNeedLogin);

  const param = getPostMsgParam(modName, funcName, reqParams as T);

  // 检查缓存
  if (cacheConfig) {
    const cacheKey = generateCacheKey(url, reqParams);
    const cachedData = cacheMap.get(cacheKey);

    if (
      cachedData &&
      !cacheConfig.forceRefresh &&
      isCacheValid(cachedData.timestamp, cacheConfig.ttl || DEFAULT_CACHE_TTL)
    ) {
      onSuccess?.(cachedData.data as R);
      return Promise.resolve(cachedData.data as R);
    }
  }

  return request
    .post(url, param)
    .then((response) => {
      const data = response.data as R & { _errno?: number };

      // 如果存在错误码，直接调用 onError 并拒绝 Promise
      if (data._errno) {
        onError?.(new Error(`Error code: ${data._errno}`) as AxiosError);
        return Promise.reject(data);
      }

      // 如果配置了缓存，则保存到缓存中
      if (cacheConfig) {
        const cacheKey = generateCacheKey(url, reqParams);
        cacheMap.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });
      }

      onSuccess?.(data as R);
      return data;
    })
    .catch(async (err: AxiosError) => {
      if (!isServer) {
        handleError(err);
      }
      if (err.code === "ECONNABORTED") {
        onTimeout?.();
      } else {
        onError?.(err);
      }
      return Promise.reject(err);
    });
};

export const reqGet = <T, R>(
  reqParams: T,
  modName: string,
  funcName: string,
  isNeedLogin?: boolean,
  onTimeout?: () => void,
  onError?: (error: AxiosError) => void,
  cacheConfig?: CacheConfig,
  onSuccess?: (data: R) => void,
) => {
  let url = getHallHttpUrl(modName, funcName, isNeedLogin);

  url =
    url +
    getGetMsgParam(reqParams as Record<string, string | number | boolean>);

  // 添加版本参数，使用正确的分隔符
  if (configStore.configVersion && configStore.configVersion.version) {
    url +=
      (url.includes("?") ? "&" : "?") +
      "v=" +
      configStore.configVersion.version;
  }

  // 检查缓存
  if (cacheConfig) {
    const cacheKey = generateCacheKey(url, reqParams);
    const cachedData = cacheMap.get(cacheKey);

    if (
      cachedData &&
      !cacheConfig.forceRefresh &&
      isCacheValid(cachedData.timestamp, cacheConfig.ttl || DEFAULT_CACHE_TTL)
    ) {
      onSuccess?.(cachedData.data as R);
      return Promise.resolve(cachedData.data as R);
    }
  }

  return request
    .get(url)
    .then((response) => {
      const data = response.data as R;

      // 如果配置了缓存，则保存到缓存中
      if (cacheConfig) {
        const cacheKey = generateCacheKey(url, reqParams);
        cacheMap.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });
      }
      onSuccess?.(data as R);

      return data;
    })
    .catch((err: AxiosError) => {
      if (!isServer) {
        handleError(err);
      }
      if (err.code === "ECONNABORTED") {
        onTimeout?.();
      } else {
        onError?.(err);
      }
      return Promise.reject(err);
    });
};

// 清除指定请求的缓存
export const clearRequestCache = (url: string, params?: unknown) => {
  const cacheKey = generateCacheKey(url, params);
  cacheMap.delete(cacheKey);
};

// 清除所有缓存
export const clearAllCache = () => {
  cacheMap.clear();
};
